import { ChevronDown, GroupPeople, SearchLoop } from 'assets/icons';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import StyledInput from 'components/styledInput/StyledInput';
import { foldText } from 'helper/foldText';
import { COLLABORATOR_ROLE, COMMERCIAL_ROLE, RECRUITER_ROLE, SUPER_ADMIN_ROLE } from 'helper/roles';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';

const fullName = (person) => `${person.firstname} ${person.lastname}`.trim();

// The colleagues by job, in this order; a role outside them closes the list,
// without a heading.
const ROLE_GROUPS = [
  { key: 'peopleCommercials', role: COMMERCIAL_ROLE },
  { key: 'peopleRecruiters', role: RECRUITER_ROLE },
  { key: 'peopleManagers', role: SUPER_ADMIN_ROLE },
  { key: 'peopleCollaborators', role: COLLABORATOR_ROLE },
];
const GROUPED_ROLES = ROLE_GROUPS.map((group) => group.role);

/**
 * Whose tasks an agency manager looks at: boxes for the whole team, yourself
 * and each colleague. The choice applies when the list closes. `value`:
 * 'team' or a list of ids. `fallback`: what nothing ticked means, yourself
 * by default; 'team' where you are not in the list (the commercials of the
 * statistics). The colleagues are grouped by job (ROLE_GROUPS), with a
 * heading once there is more than one group. As the company picker of a
 * group (CompanyPickMenu): a search bar on top, the list of limited height
 * that scrolls. `single`: one person only (the planning), `value` their id,
 * a click picks them and closes the list; no « whole team ». `ariaLabel`
 * names the button. `allLabel` names the whole list (« Tous les commerciaux »
 * where it holds only them).
 */
const PeopleSelect = ({ team, meId, value, onChange, fallback, single = false, ariaLabel, allLabel }) => {
  const { text } = useText('tasks');
  const everyoneLabel = allLabel ?? text('peopleTeam');
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(value);
  const [search, setSearch] = useState('');
  const box = useRef(null);
  const inputRef = useRef(null);
  const everyone = !single && picked === 'team';
  const ids = single ? [value] : everyone ? team.map((person) => person.id) : picked;

  useEffect(() => setPicked(value), [value]);
  useEffect(() => {
    if (open) inputRef.current?.focus();
    else setSearch('');
  }, [open]);
  const apply = () => {
    setOpen(false);
    if (single) return;
    const next = everyone || (team.length > 1 && ids.length === team.length) ? 'team' : ids.length ? ids : fallback ?? [meId];
    if (JSON.stringify(next) !== JSON.stringify(value)) onChange(next);
  };
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!box.current?.contains(event.target)) apply();
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }); // the listener follows the choice being made

  const toggle = (id) => {
    if (single) {
      setOpen(false);
      if (id !== value) onChange(id);
      return;
    }
    setPicked(ids.includes(id) ? ids.filter((other) => other !== id) : [...ids, id]);
  };
  const query = foldText(search.trim());
  const matches = (person) => !query || foldText(fullName(person)).includes(query);
  const me = team.find((person) => person.id === meId && matches(person));
  const others = team.filter((person) => person.id !== meId && matches(person));
  const groups = [
    ...ROLE_GROUPS.map((group) => ({
      key: group.key,
      people: others.filter((person) => person.role === group.role),
    })),
    { key: null, people: others.filter((person) => !GROUPED_ROLES.includes(person.role)) },
  ].filter((group) => group.people.length > 0);
  const headed = groups.filter((group) => group.key).length > 1;
  const chosen = team.filter((person) => ids.includes(person.id));
  const label = everyone
    ? everyoneLabel
    : single && chosen.length === 1 && chosen[0].id !== meId
      ? fullName(chosen[0])
    : chosen.length === 1 && chosen[0].id === meId
      ? text('peopleMe', { name: chosen[0].firstname })
      : chosen.map((person) => (person.id === meId ? text('peopleMeShort') : person.firstname)).join(', ');
  // One person: a row that picks, the current one marked; several: a box each.
  const row = (key, checked, onToggle, avatar, name) =>
    single ? (
      <button
        key={key}
        type="button"
        role="option"
        aria-selected={checked}
        onClick={onToggle}
        className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 ${checked ? 'bg-gray-50 font-medium' : ''}`}
      >
        {avatar}
        <span className="truncate">{name}</span>
      </button>
    ) : (
    <label key={key} className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100">
      <input type="checkbox" className="checkbox-primary" checked={checked} onChange={onToggle} />
      {avatar}
      <span className="truncate">{name}</span>
    </label>
    );

  return (
    <div ref={box} className="relative shrink-0">
      <button
        type="button"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? apply() : setOpen(true))}
        // One width, whoever is shown: the bar does not move when the choice changes.
        className="flex h-10 w-60 items-center gap-2 rounded-md border border-gray-200 bg-white pl-2.5 pr-3 text-sm text-gray-700 transition-colors hover:bg-gray-50"
      >
        {everyone ? (
          <span className="flex size-6 items-center justify-center rounded-full bg-gray-100">
            <GroupPeople wh={14} color="#374151" />
          </span>
        ) : (
          <span className="flex -space-x-1.5">
            {chosen.slice(0, 3).map((person) => (
              <span key={person.id} className="rounded-full ring-2 ring-white">
                <EmployeeAvatar employee={person} />
              </span>
            ))}
          </span>
        )}
        <span className="min-w-0 flex-1 truncate text-left">{label}</span>
        <ChevronDown wh={16} color="#9CA3AF" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 flex w-72 flex-col rounded-lg border border-gray-100 bg-white shadow-lg">
          <div className="p-2">
            <StyledInput
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={text('peopleSearch')}
              imgBack={<SearchLoop wh={16} color="#6B7280" />}
              inputRef={inputRef}
            />
          </div>
          <div className="max-h-80 overflow-y-auto pb-1">
            {!query && !single && row(
              'team',
              everyone,
              () => setPicked(everyone ? (fallback === 'team' ? [] : [meId]) : 'team'),
              <span className="flex size-6 items-center justify-center rounded-full bg-gray-100">
                <GroupPeople wh={14} color="#374151" />
              </span>,
              everyoneLabel,
            )}
            {!query && !single && <div className="my-1 border-t border-gray-100" />}
            {!me && !others.length && <p className="px-3 py-2 text-sm text-gray-500">{text('peopleEmpty')}</p>}
            {me && row(me.id, ids.includes(me.id), () => toggle(me.id), <EmployeeAvatar employee={me} />, text('peopleMe', { name: me.firstname }))}
            {groups.map((group) => (
              // Each heading stays on top while its group scrolls under it, the
            // next one pushing it away: even padding, so it sits the same
            // stuck or not.
            <div key={group.key ?? 'other'} role="group" aria-label={group.key ? text(group.key) : undefined}>
                {headed && group.key && (
                  <p className="sticky top-0 z-10 bg-white px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-gray-400">{text(group.key)}</p>
                )}
                {group.people.map((person) =>
                  row(person.id, ids.includes(person.id), () => toggle(person.id), <EmployeeAvatar employee={person} />, fullName(person)),
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PeopleSelect;
